"use client"

import { usePathname } from "next/navigation"
import { asLink, isFilled } from "@prismicio/client"
import { MenuIcon } from "lucide-react"
import { PrismicNextImage, PrismicNextLink } from "@prismicio/next"
import { useState } from "react"
import { LazyMotion, domAnimation, m } from "motion/react"
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet"
import { vFadeIn, vFadeUp, vStaggerContainer } from "@/lib/animations"
import { SiteLogo } from "@/components/site-logo"
import type { LayoutDocument } from "@/prismicio-types"

type HeaderClientProps = {
  layout: LayoutDocument["data"]
}

export const HeaderClient = ({ layout }: HeaderClientProps) => {
  const { logo, header_menu_item, social_media_link } = layout
  const [open, setOpen] = useState(false)
  const pathname = usePathname()

  return (
    <LazyMotion features={domAnimation}>
      <>
        {/* ── Desktop / mobile header ── */}
        <header className="sticky top-0 z-50 border-b border-amber-900/20 bg-white/95 shadow-sm backdrop-blur-sm">
          <div className="container flex items-center justify-between gap-4 py-2 md:py-2.5 lg:py-3.5 xl:py-4">
            {/* Logo + name */}
            <m.div variants={vFadeIn} initial="hidden" animate="visible">
              <SiteLogo
                image={logo}
                title={layout.title}
                subtitle={layout.subtitle}
                variant="header"
              />
            </m.div>

            <div className="flex flex-row gap-6 sm:gap-7 md:gap-8 lg:gap-9 xl:gap-10">
              {/* Desktop nav */}
              <m.nav
                className="hidden items-center gap-1 lg:flex"
                variants={vStaggerContainer}
                initial="hidden"
                animate="visible"
              >
                {header_menu_item.map((item, idx) => {
                  const isLast = idx === header_menu_item.length - 1
                  const isActive = pathname === asLink(item)
                  return (
                    <m.div key={idx} variants={vFadeUp}>
                      <PrismicNextLink
                        field={item}
                        aria-current={isActive ? "page" : undefined}
                        className={
                          isLast
                            ? "rounded-full bg-amber-100 px-5 py-2 font-medium text-amber-900 no-underline transition-colors hover:bg-amber-200 hover:no-underline"
                            : isActive
                              ? "rounded-md px-4 py-2 font-medium text-amber-800 no-underline"
                              : "rounded-md px-4 py-2 font-medium text-gray-700 no-underline transition-colors hover:text-amber-800 hover:no-underline"
                        }
                      />
                    </m.div>
                  )
                })}
              </m.nav>

              {/* Social icons (desktop) + hamburger (mobile) */}

              <m.div
                className="hidden items-center gap-4 lg:flex"
                variants={vStaggerContainer}
                initial="hidden"
                animate="visible"
              >
                {social_media_link.map((item, idx) => (
                  <m.div key={idx} variants={vFadeIn}>
                    <PrismicNextLink
                      field={item.link}
                      className="flex size-6 items-center text-amber-800 no-underline transition-colors hover:text-amber-600 hover:no-underline md:size-7 xl:size-8"
                    >
                      {isFilled.image(item.icon) && (
                        <PrismicNextImage
                          field={item.icon}
                          width={22}
                          height={22}
                          fallbackAlt=""
                          className="size-full object-contain"
                          imgixParams={{ auto: ["format", "compress"], q: 75 }}
                        />
                      )}
                    </PrismicNextLink>
                  </m.div>
                ))}
              </m.div>

              <button
                className="block rounded-md p-2 text-amber-800 transition-colors hover:bg-amber-50 hover:text-amber-600 lg:hidden"
                onClick={() => setOpen(true)}
                aria-label="Open navigation menu"
              >
                <MenuIcon className="size-6" />
              </button>
            </div>
          </div>
        </header>

        {/* ── Mobile drawer ── */}
        <Sheet open={open} onOpenChange={setOpen}>
          <SheetContent
            side="right"
            className="flex w-72 flex-col gap-4 bg-white"
          >
            <SheetHeader className="pb-4">
              <SheetTitle className="text-left text-amber-800">
                Navigation
              </SheetTitle>
            </SheetHeader>

            <nav className="flex flex-col gap-4 px-2">
              {header_menu_item.map((item, idx) => {
                const isLast = idx === header_menu_item.length - 1
                const isActive = pathname === asLink(item)
                return (
                  <PrismicNextLink
                    key={idx}
                    field={item}
                    onClick={() => setOpen(false)}
                    aria-current={isActive ? "page" : undefined}
                    className={
                      isLast
                        ? "rounded-full bg-amber-100 px-5 py-2 font-medium text-amber-900 no-underline transition-colors hover:bg-amber-200 hover:no-underline"
                        : isActive
                          ? "rounded-md px-4 py-2 font-medium text-amber-800 no-underline"
                          : "rounded-md px-4 py-2 font-medium text-gray-700 no-underline transition-colors hover:text-amber-800 hover:no-underline"
                    }
                  />
                )
              })}
            </nav>

            {social_media_link.length > 0 && (
              <div className="mt-8 flex items-center gap-3 px-6">
                {social_media_link.map((item, idx) => (
                  <m.div key={idx} variants={vFadeIn}>
                    <PrismicNextLink
                      field={item.link}
                      className="flex size-6 items-center text-amber-800 no-underline transition-colors hover:text-amber-600 hover:no-underline md:size-7 xl:size-8"
                    >
                      {isFilled.image(item.icon) && (
                        <PrismicNextImage
                          field={item.icon}
                          width={22}
                          height={22}
                          fallbackAlt=""
                          className="size-full object-contain"
                          imgixParams={{ auto: ["format", "compress"], q: 75 }}
                        />
                      )}
                    </PrismicNextLink>
                  </m.div>
                ))}
              </div>
            )}
          </SheetContent>
        </Sheet>
      </>
    </LazyMotion>
  )
}
